/**
 * Sprint 3 — Marketplace Deep-Topic Pages
 *
 * Generates 7 marketplaces × 10 sub-topics = 70 authoritative sub-topic pages.
 *
 * Structure:
 *  - MARKETPLACES: 7 canonical records with all facts (commission, timeline, returns %,
 *    payment cycle, categories, warehouse locations, API status, etc.)
 *  - TOPICS: 10 topic definitions with content generators
 *  - TOPIC_PAGES (memoized): { slug, marketplace, topic, h1, metaTitle,
 *    metaDescription, aiSummary, sections, faqs, updated } records
 *
 * URL pattern: `/{marketplace-slug}-{topic-slug}` e.g. `/myntra-commission`
 */

// ============================================================================
// MARKETPLACES — canonical facts per marketplace
// ============================================================================
export const MARKETPLACES = [
  {
    slug: 'myntra',
    name: 'Myntra',
    parent: 'Flipkart Group',
    focus: 'Fashion, footwear, beauty, accessories',
    launched: 2007,
    mau: '70M+',
    gmv: '₹35,000 Cr (FY24)',
    approvalTimeline: '10–15 days (agency-filed) · 60–90 days (self-filed)',
    commissionRange: '18–32%',
    commissionMedian: '25%',
    paymentCycle: '15–20 days (via Myntra Trust)',
    returnsRate: '25–40% for fashion, 30–45% for footwear',
    returnsPolicy: '30-day easy return with try-and-buy for select categories',
    shippingModel: 'JIT (Just-In-Time) + Warehouse (Myntra Warehouse)',
    apiStatus: 'REST API + Partner Portal + Bulk XLSX upload',
    documentsRequired: ['GST Certificate', 'Trademark (Class 25 for apparel; Class 3 for beauty)', 'Brand website + Instagram (10K+ followers preferred)', 'Cancelled cheque + Bank details', 'PAN + Company registration (COI / Partnership deed / LLP agreement)', 'Product images (minimum 20 SKU-ready)', 'Brand deck (5–10 slides)', 'Category buyer approval letter (agency-facilitated)'],
    warehouseLocations: 'Gurgaon, Bengaluru, Mumbai',
    categoryBuyers: 'Yes — dedicated category buyer team',
    penetration: 'Tier-1 + Tier-2 dominant',
    advertisingProducts: 'PLA (Product Listing Ads), Sponsored Brand, Brand Store, EDM, Homepage banner',
    hubUrl: '/myntra-account-management',
  },
  {
    slug: 'nykaa',
    name: 'Nykaa',
    parent: 'FSN E-Commerce Ventures',
    focus: 'Beauty, personal care, wellness (Nykaa) + Fashion (Nykaa Fashion)',
    launched: 2012,
    mau: '30M+',
    gmv: '₹11,200 Cr (FY24)',
    approvalTimeline: '20–25 days (agency-filed) · 45–60 days (self-filed)',
    commissionRange: '25–48%',
    commissionMedian: '35%',
    paymentCycle: '20–30 days',
    returnsRate: '12–18% (beauty); 25–35% (fashion)',
    returnsPolicy: '14-day return; opened cosmetics non-returnable',
    shippingModel: 'Warehouse-only (Nykaa Warehouse in Mumbai + Bengaluru + Delhi NCR)',
    apiStatus: 'Nykaa Seller Panel + Bulk XLSX + limited API',
    documentsRequired: ['GST Certificate', 'Trademark (Class 3 for cosmetics)', 'CDSCO License + BIS (for domestic mfg beauty)', 'FSSAI (for ingestibles)', 'Brand deck + 5-year vision', 'Instagram (25K+ preferred)', 'Cancelled cheque + PAN', 'Ingredient/formulation disclosure sheet', 'MRP + batch labelling proof'],
    warehouseLocations: 'Mumbai (Bhiwandi), Bengaluru, Delhi NCR',
    categoryBuyers: 'Yes — split by Prestige / Masstige / Value',
    penetration: 'Tier-1 dominant',
    advertisingProducts: 'PLA, Brand Boutique, Search Ads, Category Homepage, Nykaa TV',
    hubUrl: '/nykaa-account-management',
  },
  {
    slug: 'tata-cliq',
    name: 'Tata CLiQ',
    parent: 'Tata Digital',
    focus: 'Fashion, electronics, luxury (Tata CLiQ Luxury)',
    launched: 2016,
    mau: '10M+',
    gmv: '₹2,800 Cr (FY24 est.)',
    approvalTimeline: '15–25 days (agency-filed)',
    commissionRange: '18–35%',
    commissionMedian: '25%',
    paymentCycle: '15–25 days',
    returnsRate: '15–25% (fashion); 8–14% (electronics)',
    returnsPolicy: '15-day return; electronics 7-day + warranty',
    shippingModel: 'Warehouse (Bhiwandi + Bengaluru) + Drop-ship for select brands',
    apiStatus: 'Seller Panel + Bulk upload + Integration marketplace',
    documentsRequired: ['GST + PAN', 'Trademark (mandatory)', 'Brand authorization letter (from OEM)', 'Cancelled cheque', 'MSME / Udyam registration (optional)', 'Product safety certifications (electronics: BIS)', 'Brand story + heritage docs (mandatory for CLiQ Luxury)'],
    warehouseLocations: 'Bhiwandi, Bengaluru',
    categoryBuyers: 'Yes — Fashion + Electronics + Luxury separate teams',
    penetration: 'Metros dominant',
    advertisingProducts: 'PLA, Sponsored Brand, Category-featured, Email marketing',
    hubUrl: '/marketplaces',
  },
  {
    slug: 'ajio',
    name: 'AJIO',
    parent: 'Reliance Retail (Reliance Digital)',
    focus: 'Fashion (mass + AJIO Luxe curated luxury)',
    launched: 2016,
    mau: '25M+',
    gmv: '₹14,500 Cr (FY24 est.)',
    approvalTimeline: '16–26 days (agency-filed)',
    commissionRange: '15–30%',
    commissionMedian: '22%',
    paymentCycle: '20–30 days',
    returnsRate: '28–38% (fashion)',
    returnsPolicy: '30-day return with easy pickup',
    shippingModel: 'JIT + Warehouse (AJIO Fulfilment centers)',
    apiStatus: 'AJIO Seller Central + Bulk XLSX + XML feed',
    documentsRequired: ['GST + PAN', 'Trademark (Class 25)', 'Brand deck (mandatory for AJIO Luxe)', 'Instagram + Website', 'Cancelled cheque', 'Product photography (minimum 10 SKUs, model + flat lay)', 'MRP + season plan'],
    warehouseLocations: 'Bhiwandi, Ludhiana, Bengaluru, Hyderabad',
    categoryBuyers: 'Yes — AJIO Indie + AJIO Luxe teams separate',
    penetration: 'Tier-2/3 strong (mass); Tier-1 for Luxe',
    advertisingProducts: 'PLA, Sponsored Brand, Homepage Banner, AJIO EDM',
    hubUrl: '/ajio-account-management',
  },
  {
    slug: 'blinkit',
    name: 'Blinkit',
    parent: 'Zomato',
    focus: 'Quick commerce (10-min): Grocery, FMCG, personal care, snacks',
    launched: 2013,
    mau: '15M+',
    gmv: '₹8,500 Cr (FY24 GOV)',
    approvalTimeline: '7–14 days',
    commissionRange: '12–22% (category-based)',
    commissionMedian: '17%',
    paymentCycle: '7–14 days',
    returnsRate: '2–5%',
    returnsPolicy: 'No customer returns; only quality/damage rejections at dark store',
    shippingModel: 'Dark store network (400+ across metros + Tier-2)',
    apiStatus: 'Blinkit Seller Panel + Order Management API + Dark-store inventory sync',
    documentsRequired: ['GST + FSSAI (mandatory for F&B / personal care)', 'PAN + COI', 'Cancelled cheque', 'MRP labelling + batch code proof', 'Brand authorization letter', 'Storage + supply-chain readiness (dark-store integration required)'],
    warehouseLocations: 'Distributed dark-store network across 30+ cities',
    categoryBuyers: 'Yes — Grocery + Personal Care + F&B teams',
    penetration: 'Metros + Tier-1 dominant',
    advertisingProducts: 'Product Ads, Category Featured, In-app banners, Push notifications',
    hubUrl: '/blinkit-account-management',
  },
  {
    slug: 'zepto',
    name: 'Zepto',
    parent: 'Kiranakart Technologies',
    focus: 'Quick commerce (10-min): Grocery, FMCG, personal care',
    launched: 2021,
    mau: '10M+',
    gmv: '₹5,200 Cr (FY24 est.)',
    approvalTimeline: '7–14 days',
    commissionRange: '15–25%',
    commissionMedian: '20%',
    paymentCycle: '7–14 days',
    returnsRate: '3–6%',
    returnsPolicy: 'No customer returns; only quality/damage rejections',
    shippingModel: 'Zepto dark-store network (350+ in Tier-1/2)',
    apiStatus: 'Zepto Seller Panel + Inventory sync + Order API',
    documentsRequired: ['GST + FSSAI', 'PAN + COI', 'Cancelled cheque', 'MRP + batch labelling', 'Brand authorization', 'Supply readiness for dark-store restocks (48 hrs)'],
    warehouseLocations: 'Zepto dark-stores across 25+ cities',
    categoryBuyers: 'Yes — Category Managers for FMCG + Personal Care + F&B',
    penetration: 'Tier-1 dominant, expanding to Tier-2',
    advertisingProducts: 'Product Ads, Category Sponsored, Push notifications',
    hubUrl: '/zepto-account-management',
  },
  {
    slug: 'swiggy-instamart',
    name: 'Swiggy Instamart',
    parent: 'Swiggy',
    focus: 'Quick commerce (10–15 min): Grocery, FMCG, personal care',
    launched: 2020,
    mau: '20M+',
    gmv: '₹7,800 Cr (FY24 GOV est.)',
    approvalTimeline: '7–14 days',
    commissionRange: '13–23%',
    commissionMedian: '18%',
    paymentCycle: '10–15 days',
    returnsRate: '2–5%',
    returnsPolicy: 'No customer returns; damage/quality claims settled at dark-store',
    shippingModel: 'Instamart dark-stores (500+ across 30+ cities)',
    apiStatus: 'Swiggy Seller Panel + Instamart Order API + Inventory feed',
    documentsRequired: ['GST + FSSAI', 'PAN + COI', 'Cancelled cheque', 'Brand authorization', 'MRP + labelling proof', 'Supply-chain SLA sign-off'],
    warehouseLocations: 'Instamart dark-stores across metros + Tier-2',
    categoryBuyers: 'Yes — Category Managers per vertical',
    penetration: 'Metros + Tier-1 + expanding Tier-2',
    advertisingProducts: 'Product Ads, Category Sponsored, Homepage Banners, Swiggy Ads',
    hubUrl: '/swiggy-instamart-account-management',
  },
];

// ============================================================================
// TOPICS — 10 sub-topic definitions with unique content generators
// ============================================================================
export const TOPIC_KEYS = [
  'documents-required',
  'commission',
  'pricing',
  'advertising',
  'returns',
  'payments',
  'shipping',
  'api-integration',
  'integrations',
  'automation',
];

const TOPIC_META = {
  'documents-required': {
    label: 'Documents Required',
    titleSuffix: 'Documents Required — 2026 Checklist',
    metaSuffix: 'documents required to sell — full checklist, brand approval prerequisites, GST + trademark + certifications, sample document pack (2026).',
    aiTitle: 'Documents Required',
  },
  commission: {
    label: 'Commission',
    titleSuffix: 'Commission Structure & Fees (2026 Guide)',
    metaSuffix: 'commission structure, category-wise fees, fixed fees, closing fees, payment gateway charges, and reduction strategies (2026).',
    aiTitle: 'Commission Structure',
  },
  pricing: {
    label: 'Pricing',
    titleSuffix: 'Pricing Strategy for Sellers (2026 Playbook)',
    metaSuffix: 'pricing strategy, MRP vs discount pricing, price parity rules, competitive positioning, and margin optimization (2026).',
    aiTitle: 'Pricing Strategy',
  },
  advertising: {
    label: 'Advertising & PLA',
    titleSuffix: 'Advertising Guide — PLA, Sponsored Brand, Ads (2026)',
    metaSuffix: 'advertising products, PLA ROAS benchmarks, campaign setup, budget allocation, and category-buyer alignment (2026).',
    aiTitle: 'Advertising Playbook',
  },
  returns: {
    label: 'Returns Policy',
    titleSuffix: 'Returns Policy & Management (2026 Guide)',
    metaSuffix: 'returns policy, return rate benchmarks, reduction strategies, RTO handling, and quality control (2026).',
    aiTitle: 'Returns Management',
  },
  payments: {
    label: 'Payments',
    titleSuffix: 'Payments, Payment Cycle & Reconciliation (2026)',
    metaSuffix: 'payment cycle, settlement timelines, reconciliation process, TDS/TCS deductions, and dispute resolution (2026).',
    aiTitle: 'Payments & Settlement',
  },
  shipping: {
    label: 'Shipping',
    titleSuffix: 'Shipping Guide — Warehouse, JIT, Dark-store (2026)',
    metaSuffix: 'shipping models, warehouse vs JIT vs dark-store, fulfilment SLAs, freight costs, and category-wise recommendations (2026).',
    aiTitle: 'Shipping Playbook',
  },
  'api-integration': {
    label: 'API Integration',
    titleSuffix: 'API Integration Guide — Orders, Inventory, Catalog (2026)',
    metaSuffix: 'API integration guide, endpoints, authentication, rate limits, order + inventory + catalog sync, and OMS integration (2026).',
    aiTitle: 'API Integration',
  },
  integrations: {
    label: 'Integrations',
    titleSuffix: 'Integrations: Connect OMS, ERP, WMS & Shopify in Under 15 Minutes',
    metaSuffix: 'integration ecosystem, OMS + ERP + WMS + Shopify + WooCommerce connectors, native + partner apps, and Zaroori Jini OMS (2026).',
    aiTitle: 'Integrations',
  },
  automation: {
    label: 'Automation',
    titleSuffix: 'Automation Playbook — AI, Pricing, Repricing, Ads (2026)',
    metaSuffix: 'automation workflows, AI-powered repricing, ad bidding, inventory sync, order routing, and Zaroori Jini OMS automation stack (2026).',
    aiTitle: 'Automation Playbook',
  },
};

// ============================================================================
// SECTION GENERATORS — build page sections per marketplace × topic
// Each generator returns { aiSummary, sections[], faqs[] }
// ============================================================================
const generators = {
  'documents-required': (m) => ({
    aiSummary: {
      answer: `To sell on ${m.name}, you need ${m.documentsRequired.length} core documents including GST certificate, trademark, brand authorization, cancelled cheque, and category-specific certifications. Approval timeline: ${m.approvalTimeline}. Agency-filed submissions cut timeline by 60–75% because ${m.name}'s category buyers accept pre-vetted dossiers on the first attempt.`,
      bullets: m.documentsRequired.slice(0, 5).map((d) => d),
      keyFacts: [
        { label: 'Total Documents', value: `${m.documentsRequired.length} mandatory` },
        { label: 'Timeline', value: m.approvalTimeline },
        { label: 'Trademark Class', value: m.focus.toLowerCase().includes('beauty') ? 'Class 3 (cosmetics)' : 'Class 25 (apparel)' },
        { label: 'GST', value: 'Mandatory' },
        { label: 'Instagram Threshold', value: '10K–25K followers preferred' },
        { label: 'Updated', value: 'February 2026' },
      ],
    },
    sections: [
      { type: 'para', body: `${m.name} — India's ${m.focus.toLowerCase()} marketplace under ${m.parent} with ${m.mau} MAU — has one of the most rigorous seller onboarding processes in Indian e-commerce. Every brand goes through a documented approval workflow before catalog activation.` },
      { type: 'h2', text: `Complete ${m.name} Document Checklist (Feb 2026)` },
      { type: 'checklist', items: m.documentsRequired },
      { type: 'h2', text: 'Timeline breakdown' },
      { type: 'para', body: `Standard timeline: ${m.approvalTimeline}. The difference between agency-filed and self-filed comes down to category-buyer relationships and pre-vetted brand dossiers. Zaroori Retail has direct relationships with ${m.name}'s category buyers, which unlocks the faster tier.` },
      { type: 'h2', text: 'How to prepare documents in 7 days' },
      { type: 'steps', steps: [
        { title: 'Day 1–2: GST + PAN', body: 'Verify GST is active and matches company legal name. Fetch fresh GST certificate from portal.' },
        { title: 'Day 3: Trademark', body: `Confirm trademark is registered (not just applied). ${m.name} rejects "TM applied" for premium categories. Class 25 for apparel, Class 3 for beauty.` },
        { title: 'Day 4: Brand deck + Instagram audit', body: 'Prepare 5-10 slide brand deck. Grow Instagram to 10K+ (organic + collabs). Add professional Highlights.' },
        { title: 'Day 5: Product photography', body: 'Shoot 20 SKUs to marketplace-grade photography (1080×1440, white BG for hero, 4 lifestyle angles).' },
        { title: 'Day 6: Bank + legal docs', body: 'Cancelled cheque + updated COI/LLP agreement + brand authorization letter (if manufacturing outsourced).' },
        { title: 'Day 7: Final compilation', body: 'Compile everything into a single labelled ZIP + submit via agency for category-buyer intro.' },
      ]},
      { type: 'h2', text: 'Common rejection reasons' },
      { type: 'bullets', items: [
        'Trademark still in "applied" stage — need registered mark',
        'Instagram under 10K or missing brand identity',
        'Product photography below marketplace grade',
        'GST + PAN name mismatch',
        'Missing category-specific certifications (CDSCO for cosmetics, FSSAI for F&B, BIS for electronics)',
      ]},
    ],
    faqs: [
      { q: `What documents are mandatory for ${m.name} brand approval?`, a: `${m.documentsRequired.slice(0, 4).join(', ')} — plus category-specific certifications (Class 3 trademark for beauty, FSSAI for ingestibles, CDSCO for cosmetics).` },
      { q: `How long does ${m.name} take to approve documents?`, a: `${m.approvalTimeline}. Agency-filed dossiers are pre-vetted by category buyers, cutting timeline by 60–75%.` },
      { q: `Can I sell on ${m.name} without a trademark?`, a: `No — ${m.name} requires a registered trademark (not "applied"). Class 25 for apparel/footwear, Class 3 for cosmetics/beauty, Class 5 for wellness/pharma.` },
      { q: `Is Instagram followers a hard requirement for ${m.name}?`, a: `Not formally, but ${m.name}'s category buyers use social proof as a filter. Brands with 10K–25K+ Instagram followers get faster approval and better catalog placement.` },
      { q: `How can Zaroori Retail help with ${m.name} documents?`, a: `We audit your document pack against ${m.name}'s current buyer criteria (updated Feb 2026), fix gaps in 5–7 working days, and file via our direct category-buyer relationships — targeting first-attempt approval in ${m.approvalTimeline.split(' · ')[0] || m.approvalTimeline}.` },
    ],
  }),

  commission: (m) => ({
    aiSummary: {
      answer: `${m.name} commission ranges from ${m.commissionRange} depending on category, with a marketplace median of ${m.commissionMedian}. On top of commission, sellers pay fixed fees (packaging + handling), closing fees, and payment gateway charges — totalling roughly commission + 3–5%. Payment cycle: ${m.paymentCycle}. Category buyers can negotiate 2–4 percentage points off commission for premium brands with growing GMV.`,
      bullets: [
        `Base commission: ${m.commissionRange}`,
        `Category median: ${m.commissionMedian}`,
        `Payment cycle: ${m.paymentCycle}`,
        `Additional fees: fixed fee + closing fee + PG charges (~3–5%)`,
        `Negotiable via category-buyer relationships (2–4 pp reduction for premium brands)`,
      ],
      keyFacts: [
        { label: 'Commission Range', value: m.commissionRange },
        { label: 'Median', value: m.commissionMedian },
        { label: 'Payment Cycle', value: m.paymentCycle },
        { label: 'Fixed Fees', value: '₹15–65 per unit' },
        { label: 'Closing Fees', value: '0.5–2% per order' },
        { label: 'Updated', value: 'February 2026' },
      ],
    },
    sections: [
      { type: 'para', body: `${m.name}'s commission structure is category-first: within its ${m.focus.toLowerCase()} focus, apparel takes ${m.commissionRange.split('–')[0]}, premium/luxury pushes toward ${m.commissionRange.split('–')[1]}. Sellers routinely underestimate the "commission-plus" load — fixed fee + closing fee + PG easily add 3–5% on top of headline commission.` },
      { type: 'h2', text: 'Commission breakdown by fee type' },
      { type: 'table', headers: ['Fee Type', 'Range', 'When Charged'], rows: [
        ['Base commission', m.commissionRange, 'Per unit sold'],
        ['Fixed fee', '₹15–65', 'Per unit shipped'],
        ['Closing fee', '0.5–2%', 'Per order value'],
        ['Payment gateway', '1.5–2.5%', 'Per successful payment'],
        ['Return processing', '₹40–120', 'Per returned unit'],
      ]},
      { type: 'h2', text: 'How to reduce commission on ' + m.name },
      { type: 'steps', steps: [
        { title: 'Build 6-month growing GMV', body: `Category buyers renegotiate for brands showing 25%+ MoM GMV growth over 6 months on ${m.name}.` },
        { title: 'Increase basket size', body: 'Higher-AOV categories carry lower % commission absolutely. Push bundles + higher-price SKUs.' },
        { title: 'Reduce returns', body: 'Below-median return rate improves seller score → unlocks negotiation leverage.' },
        { title: 'Category-buyer intro', body: 'Direct category-buyer relationships (via agency) unlock 2–4 pp commission reduction and PLA credits.' },
      ]},
      { type: 'h2', text: 'Category-wise commission (indicative)' },
      { type: 'table', headers: ['Category', 'Commission', 'Notes'], rows: buildCategoryCommissionRows(m) },
    ],
    faqs: [
      { q: `What is ${m.name}'s commission rate?`, a: `${m.commissionRange} depending on category. Median: ${m.commissionMedian}.` },
      { q: `Are ${m.name} fees negotiable?`, a: 'Yes — for brands with 6+ months of growing GMV, ≥25% MoM growth, and below-median return rate. Category-buyer relationships (via agency) are the primary lever for negotiation.' },
      { q: `What is the total effective cost of selling on ${m.name}?`, a: `Commission + fixed fee + closing fee + PG ≈ ${m.commissionRange.split('–')[0].replace('%', '')}–${parseInt(m.commissionRange.split('–')[1]) + 5}% of order value once all fees are stacked.` },
      { q: `When does ${m.name} settle payments?`, a: `${m.paymentCycle}, subject to return window closure and deductions.` },
      { q: `Can Zaroori Retail help renegotiate ${m.name} commission?`, a: 'Yes — we prepare category-buyer decks with your GMV curve, return-rate benchmark, and category penetration data, then intro to the relevant buyer for a formal review.' },
    ],
  }),

  pricing: (m) => ({
    aiSummary: {
      answer: `Pricing strategy on ${m.name} is dictated by three constraints: (a) marketplace price-parity rules — MRP must match your D2C site + other marketplaces within ±3%; (b) discount ladder — ${m.name}'s promo engine expects 15–35% off MRP for "core" days and 40–60% off during sale events; (c) category benchmarks — under-discounting kills discoverability, over-discounting kills margin. A structured MRP → discount → sale ladder is the difference between profitable and loss-making SKUs.`,
      bullets: [
        'Price parity within ±3% across marketplaces + D2C',
        'Core discount ladder: 15–35% off MRP (regular days)',
        'Sale event discount: 40–60% off MRP (EORS/BFCM/festive)',
        'MRP inflation to enable discount ladder is standard practice',
        'Below-category-median pricing kills margin without moving GMV',
      ],
      keyFacts: [
        { label: 'Parity Rule', value: '±3% across channels' },
        { label: 'Regular Discount', value: '15–35% off MRP' },
        { label: 'Sale Event Discount', value: '40–60% off MRP' },
        { label: 'MRP Update Frequency', value: 'Quarterly recommended' },
        { label: 'Category Median', value: 'Available on request' },
        { label: 'Updated', value: 'February 2026' },
      ],
    },
    sections: [
      { type: 'para', body: `On ${m.name}, pricing is not a "set once" decision. The marketplace's discovery + ads engine reads your MRP × discount × conversion signals every 24 hours. Overpriced SKUs get demoted; underpriced SKUs bleed margin. The correct strategy is a structured MRP → discount → sale ladder tied to category benchmarks and margin math.` },
      { type: 'h2', text: `${m.name} Discount Ladder Playbook` },
      { type: 'table', headers: ['Event Window', 'Discount %', 'Purpose'], rows: [
        ['Regular days', '15–25%', 'Baseline conversion + AOV'],
        ['Weekend push', '20–30%', 'Traffic + repeat visits'],
        ['Category promo', '30–45%', 'Category-buyer aligned'],
        ['Sale event (EORS/BFCM)', '40–60%', 'GMV surge + rank push'],
        ['Clearance', '55–70%', 'Aged stock rotation'],
      ]},
      { type: 'h2', text: 'How to set MRP correctly' },
      { type: 'steps', steps: [
        { title: 'Landed cost + margin', body: 'Landed cost × (1 + margin %) = "true price". Never discount below true price + commission + fees.' },
        { title: 'MRP inflation for discount runway', body: 'MRP = true price ÷ 0.65 to enable 35% "regular" discount without hurting margin.' },
        { title: 'Category benchmark', body: 'Compare against top 20 SKUs in your category. Land between median and top-quartile.' },
        { title: 'Parity audit', body: 'Once/week automated audit across marketplaces + D2C. Tolerance: ±3% or category buyer flag.' },
      ]},
      { type: 'h2', text: 'Common pricing mistakes on ' + m.name },
      { type: 'bullets', items: [
        'Setting MRP too close to true price → no discount runway',
        'Blanket 40% off across catalog → destroys perception of "value on sale"',
        'Not updating MRP quarterly → margin decay',
        'Undercutting D2C site → violates parity + kills own conversion',
      ]},
    ],
    faqs: [
      { q: `What is ${m.name}'s pricing parity rule?`, a: `MRP + discounted price must match your D2C site + other marketplaces within ±3%. Repeated violations trigger category-buyer review + potential delisting.` },
      { q: 'Should MRP be higher than D2C site?', a: `MRP should be identical across channels. Only the discount % may differ, and even that within ±3%. Structured ladder inflation is fine when consistent across channels.` },
      { q: `What is the ideal discount % for ${m.name}?`, a: `15–35% off MRP on regular days, 40–60% during sale events. Category benchmarks vary — request a benchmark report for your category.` },
      { q: 'How often should I revise pricing?', a: `Quarterly full audit; ad-hoc revisions for aged SKUs (>90 days on shelf). ${m.name}'s ranking engine rewards regularly-updated catalogs.` },
      { q: 'Can Zaroori Retail run a pricing audit?', a: 'Yes — we benchmark your top 50 SKUs against category leaders, model margin at every discount tier, and deliver a 90-day pricing calendar tied to sale events + PLA plan.' },
    ],
  }),

  advertising: (m) => ({
    aiSummary: {
      answer: `${m.name}'s ad stack — ${m.advertisingProducts} — delivers 3–5x ROAS when properly configured. Winning brands allocate 8–15% of GMV to ads (higher in launch mode), split roughly 60% PLA, 20% Sponsored Brand, 20% category/homepage. Category-buyer alignment doubles PLA ROAS because premium placements + PLA credits are gate-kept by the category team.`,
      bullets: [
        `Ad products: ${m.advertisingProducts}`,
        'Winning ROAS: 3–5x (mature); 1.8–2.5x (launch)',
        'Budget mix: 60% PLA + 20% Sponsored Brand + 20% category/homepage',
        'Category buyer approval unlocks PLA credits + premium placements',
        `Optimal spend: 8–15% of ${m.name} GMV`,
      ],
      keyFacts: [
        { label: 'Winning ROAS', value: '3–5x mature; 1.8–2.5x launch' },
        { label: 'Recommended Spend', value: '8–15% of GMV' },
        { label: 'PLA Share', value: '60% of ad budget' },
        { label: 'Best Bid Model', value: 'Manual for launch → auto-bid for scale' },
        { label: 'CPC Range', value: '₹8–35' },
        { label: 'Updated', value: 'February 2026' },
      ],
    },
    sections: [
      { type: 'para', body: `${m.name}'s ad system is a merit-based ranking overlay. PLA bids interact with organic quality score (conversion rate + return rate + review score) to determine placement. Brands treating ads as "additive discovery" outperform those treating ads as "buy your way in" by 2–3x.` },
      { type: 'h2', text: `${m.name} Ad Products` },
      { type: 'table', headers: ['Product', 'Purpose', 'Typical CPC / CPM'], rows: [
        ['PLA (Product Listing Ads)', 'Search + browse discovery', '₹8–35 CPC'],
        ['Sponsored Brand', 'Brand awareness at top of search', '₹150–400 CPM'],
        ['Brand Store', 'Curated brand hub for repeat visits', '₹50k–5L monthly retainer'],
        ['Homepage Banner', 'High-visibility hero placement', '₹1L–15L per slot'],
        ['Category Featured', 'Category-level placement', 'Category-buyer allocated'],
      ]},
      { type: 'h2', text: '90-Day launch ads plan' },
      { type: 'steps', steps: [
        { title: 'Day 1–15: Baseline PLA at manual bids', body: 'Set manual CPC at 1.3× category median. Run all 25–50 hero SKUs. Target 1.8x ROAS.' },
        { title: 'Day 16–30: Split-test creatives', body: 'A/B lifestyle vs flatlay hero. Kill bottom-30% CTR SKUs. Refresh underperformers.' },
        { title: 'Day 31–60: Sponsored Brand + category alignment', body: 'Add Sponsored Brand for top 10 SKUs. Meet category buyer to unlock PLA credits.' },
        { title: 'Day 61–90: Auto-bid + scale', body: 'Move top ROAS SKUs to auto-bid + max scale. Introduce Brand Store for repeat traffic.' },
      ]},
      { type: 'h2', text: 'Common ad mistakes on ' + m.name },
      { type: 'bullets', items: [
        'Running ads on SKUs with <2 reviews → wastes budget until organic proof',
        'Auto-bid from day 1 → wastes budget on low-conversion queries',
        'Not aligning with category buyer → misses PLA credits + premium placements',
        'Treating ROAS as sole metric → ignores rank + review velocity impact',
      ]},
    ],
    faqs: [
      { q: `What is the average ROAS on ${m.name} PLA?`, a: `Mature accounts: 3–5x. Launch mode: 1.8–2.5x. Category-buyer-aligned accounts see 30–50% higher ROAS due to PLA credits + premium placements.` },
      { q: `How much should I spend on ${m.name} ads?`, a: `8–15% of ${m.name} GMV. Launch: 15–25% for the first 90 days to build rank + review velocity.` },
      { q: 'Should I run PLA on SKUs with no reviews?', a: 'No — wait for organic proof (min 2 reviews, 4.0+ rating) before scaling ads on that SKU. Instead, run ads on hero SKUs and let organic traffic build reviews for the rest.' },
      { q: `What is Category Buyer alignment on ${m.name}?`, a: `Direct relationship with ${m.name}'s category buyer team — unlocks PLA credits, homepage banner slots, EDM inclusion, and preview-access to sale event calendars.` },
      { q: `Can Zaroori Retail run ${m.name} ads for me?`, a: 'Yes — our marketplace ads pod runs PLA + Sponsored Brand + category alignment on retainer, with weekly MOM reports and monthly ROAS reviews.' },
    ],
  }),

  returns: (m) => ({
    aiSummary: {
      answer: `${m.name}'s return rate is ${m.returnsRate}. Policy: ${m.returnsPolicy}. Return rate is the single biggest silent margin killer — each return costs ₹80–250 in reverse logistics + repackaging + margin loss. Winning brands attack root causes (fit + description accuracy + size chart + quality) rather than just accepting returns as a fixed cost.`,
      bullets: [
        `Category return rate: ${m.returnsRate}`,
        `Policy: ${m.returnsPolicy}`,
        'Cost per return: ₹80–250 (reverse logistics + repackaging)',
        'Below-category-median return rate → category-buyer favour',
        'Root causes: fit (60%), description mismatch (20%), quality (15%), other (5%)',
      ],
      keyFacts: [
        { label: 'Return Rate', value: m.returnsRate },
        { label: 'Policy', value: m.returnsPolicy },
        { label: 'Cost per Return', value: '₹80–250' },
        { label: 'Best-in-class', value: 'Category median − 10 pp' },
        { label: 'Root Cause #1', value: 'Fit (60%)' },
        { label: 'Updated', value: 'February 2026' },
      ],
    },
    sections: [
      { type: 'para', body: `${m.name}'s return economy is structural: policy generosity + shopper behavior + category-specific fit uncertainty create baseline returns of ${m.returnsRate}. But best-in-class brands operate 10 pp below category median — driving 4–8% margin uplift on the same GMV.` },
      { type: 'h2', text: 'Return rate root cause breakdown' },
      { type: 'table', headers: ['Root Cause', '% of Returns', 'Fix'], rows: [
        ['Fit / size mismatch', '60%', 'Precise size charts + fit fingers + model dimensions'],
        ['Product description mismatch', '20%', 'Rewrite copy + real-photo hero + video'],
        ['Quality issue', '15%', 'QC before dispatch + material spec disclosure'],
        ['Delivery / damage', '3%', 'Better packaging + carrier SLA audit'],
        ['Buyer remorse', '2%', 'Handled at post-order comms'],
      ]},
      { type: 'h2', text: '30-day returns reduction playbook' },
      { type: 'steps', steps: [
        { title: 'Week 1: Return reason audit', body: `Pull last 60 days of ${m.name} returns. Tag each by reason. Identify top 3 SKUs contributing 40%+ of returns.` },
        { title: 'Week 2: Size chart + fit content', body: 'Rebuild size chart with actual measurements. Add "model wears size X, height Y" callouts. Insert fit video.' },
        { title: 'Week 3: Description + hero refresh', body: 'Rewrite title + description + first 5 bullets on top 3 returners. Refresh hero photo.' },
        { title: 'Week 4: QC gate + monitoring', body: 'Introduce pre-dispatch QC checklist. Weekly return-rate dashboard.' },
      ]},
      { type: 'h2', text: `${m.name} returns policy nuances` },
      { type: 'para', body: `${m.name} settles returns on a rolling window (typically ${m.paymentCycle} + return-window buffer). Deductions for returns are auto-applied on the settlement statement. Disputed returns can be raised via category buyer if evidence (photo/video at dispatch) is available.` },
    ],
    faqs: [
      { q: `What is the average return rate on ${m.name}?`, a: `${m.returnsRate}.` },
      { q: `Who pays for return shipping on ${m.name}?`, a: `${m.returnsPolicy.includes('easy return') ? 'Marketplace absorbs return shipping for genuine reasons; seller-fault returns are deducted.' : 'Seller bears reverse logistics cost for return acceptance.'}` },
      { q: 'How can I reduce returns?', a: 'Fix fit content (size chart + video + model dims) — this alone reduces returns 15–25% because fit is 60% of return volume.' },
      { q: 'Can I dispute a wrongful return?', a: `Yes — if you have photo/video evidence of the correct product at dispatch. Raise via category buyer or seller support. Timeline: 7–15 working days for resolution.` },
      { q: 'Can Zaroori Retail help reduce my return rate?', a: 'Yes — our returns diagnostic pulls 90 days of returns, tags reason clusters, and delivers a 30-day fix plan targeting 10–20 pp reduction on top-return SKUs.' },
    ],
  }),

  payments: (m) => ({
    aiSummary: {
      answer: `${m.name} settles on a ${m.paymentCycle} cycle after return window closure. Every settlement statement includes: gross sales, commission, fixed fees, closing fees, PG charges, return deductions, TDS/TCS, and net payout. Discrepancies of 2–8% on unreconciled statements are the norm — brands leave ₹40k–₹4L/month on the table by not reconciling systematically.`,
      bullets: [
        `Payment cycle: ${m.paymentCycle}`,
        'Discrepancy rate on unreconciled statements: 2–8%',
        'Reconciliation = fees audit + missing settlements + wrong TDS/TCS',
        'GST + TDS + TCS deducted per Section 194-O',
        'Disputes must be raised within 30–45 days of statement',
      ],
      keyFacts: [
        { label: 'Payment Cycle', value: m.paymentCycle },
        { label: 'Statement Frequency', value: 'Weekly' },
        { label: 'Dispute Window', value: '30–45 days' },
        { label: 'TDS Rate', value: '0.1% (Sec 194-O)' },
        { label: 'TCS Rate', value: '0.5–1% (state-varied)' },
        { label: 'Updated', value: 'February 2026' },
      ],
    },
    sections: [
      { type: 'para', body: `${m.name} runs a rolling settlement engine: orders paid after return window closes, deductions applied automatically, net payout to your registered bank account. On paper the process is clean — in practice, 2–8% of statement line items have discrepancies. Systematic reconciliation is the difference between "we assume we're paid" and "we know we're paid".` },
      { type: 'h2', text: 'Settlement statement structure' },
      { type: 'table', headers: ['Line Item', 'Description', 'Watch For'], rows: [
        ['Gross sales', 'Order value × units sold', 'Missing orders vs your OMS'],
        ['Commission', `${m.commissionRange} category-based`, 'Wrong category classification'],
        ['Fixed fee', '₹15–65/unit', 'Applied twice'],
        ['Closing fee', '0.5–2% of order value', 'Wrong slab applied'],
        ['PG charges', '1.5–2.5%', 'Rounding errors'],
        ['Return deductions', 'Per returned unit', 'Wrongful returns not disputed'],
        ['TDS (194-O)', '0.1%', 'Correct PAN mapped'],
        ['TCS', '0.5–1%', 'State + threshold correct'],
        ['Net payout', 'Final amount to bank', 'Cross-check bank statement'],
      ]},
      { type: 'h2', text: 'Weekly reconciliation workflow' },
      { type: 'steps', steps: [
        { title: 'Pull statement + orders', body: `Download this week's ${m.name} settlement statement + your OMS order log.` },
        { title: 'Line-item match', body: 'Match every order in statement to OMS. Flag missing orders + duplicate charges.' },
        { title: 'Fee audit', body: 'Recompute commission + fees for each order. Flag deviations > ₹10.' },
        { title: 'Return audit', body: 'Cross-check return deductions against your dispatch photos/videos. File disputes for wrongful returns.' },
        { title: 'File disputes', body: 'Every discrepancy → dispute ticket via seller support with evidence. 30–45 day window.' },
      ]},
      { type: 'h2', text: 'Common reconciliation gaps' },
      { type: 'bullets', items: [
        'Return marked "customer damage" when actually seller error → dispute with dispatch photo',
        'Commission slab wrong (higher-% category applied) → dispute with category classification',
        'TDS applied twice → dispute with PAN mapping proof',
        'Missing orders in statement → typically appear in next cycle but must be tracked',
      ]},
    ],
    faqs: [
      { q: `What is ${m.name}'s payment cycle?`, a: `${m.paymentCycle}, after return window closes.` },
      { q: `How much am I losing to reconciliation gaps?`, a: `2–8% of gross ${m.name} sales for unreconciled accounts. For a brand doing ₹50L/month, that's ₹1–4L/month lost.` },
      { q: 'How do I file a dispute?', a: `Via ${m.name} Seller Support with the specific line item + evidence (dispatch photo, category classification proof, PAN mapping). 30–45 day window from statement date.` },
      { q: 'What TDS is deducted?', a: '0.1% under Section 194-O of Income Tax Act 1961 on gross sales value.' },
      { q: 'Can Zaroori Retail reconcile my statements?', a: `Yes — our reconciliation pod runs weekly reconciliation on ${m.name} + all your other marketplaces, files disputes with evidence, and recovers 2–8% of GMV that would otherwise be lost.` },
    ],
  }),

  shipping: (m) => ({
    aiSummary: {
      answer: `${m.name} operates on a ${m.shippingModel} model. Warehouse locations: ${m.warehouseLocations}. Choice between JIT (ship from seller), warehouse (stock at marketplace), and hybrid depends on category velocity, SKU count, and margin. Warehouse fulfilment gets ranking preference + faster delivery promise → 20–35% higher conversion, but costs 6–12% of GMV in storage + intake fees.`,
      bullets: [
        `Model: ${m.shippingModel}`,
        `Warehouses: ${m.warehouseLocations}`,
        'Warehouse ranking boost: 20–35% higher conversion',
        'Cost: 6–12% of GMV for storage + intake',
        'JIT recommended for slow-moving/premium SKUs',
      ],
      keyFacts: [
        { label: 'Model', value: m.shippingModel },
        { label: 'Warehouses', value: m.warehouseLocations },
        { label: 'Warehouse Boost', value: '20–35% conversion' },
        { label: 'Warehouse Cost', value: '6–12% of GMV' },
        { label: 'JIT SLA', value: '48–72 hours dispatch' },
        { label: 'Updated', value: 'February 2026' },
      ],
    },
    sections: [
      { type: 'para', body: `${m.name}'s fulfilment model — ${m.shippingModel} — dictates warehouse strategy, working-capital lock-in, and delivery promise. Top brands don't pick one — they operate a hybrid: hero SKUs in warehouse for ranking + slow-movers via JIT for capital efficiency.` },
      { type: 'h2', text: 'Fulfilment model comparison' },
      { type: 'table', headers: ['Model', 'Best For', 'Cost', 'Delivery SLA'], rows: [
        ['Warehouse', 'Hero SKUs, high velocity', '6–12% of GMV', '1–2 days'],
        ['JIT (seller-ship)', 'Slow-movers, premium', 'Own logistics cost', '3–7 days'],
        ['Drop-ship', 'Made-to-order, luxury', 'Custom rate card', '5–14 days'],
      ]},
      { type: 'h2', text: 'Warehouse setup checklist' },
      { type: 'steps', steps: [
        { title: 'Category-buyer approval for warehouse', body: `${m.name}'s warehouse team requires category-buyer sign-off. Not all sellers get warehouse access.` },
        { title: 'SKU selection', body: 'Only top-30 SKUs by velocity should go to warehouse. Rest via JIT.' },
        { title: 'Barcoding + packaging spec', body: 'Marketplace-compliant barcodes + packaging spec. Non-compliance triggers rejection at intake.' },
        { title: 'Storage + intake fees', body: 'Storage: ₹40–120/unit/month. Intake: 2–4% of MRP. Reverse logistics: ₹80–200/unit.' },
        { title: 'Inventory replenishment cadence', body: 'Weekly replenishment based on velocity forecast. Under-stock hurts rank; over-stock costs money.' },
      ]},
      { type: 'h2', text: 'Common shipping gotchas' },
      { type: 'bullets', items: [
        'Under-stocking → SKU goes out-of-stock → ranking collapse → 2–3 weeks to recover',
        'Wrong barcoding → intake rejection + return-to-seller freight',
        'JIT dispatch > 72 hours → seller score drops',
        'Warehouse for slow-movers → dead capital + storage fee drag',
      ]},
    ],
    faqs: [
      { q: `What shipping models does ${m.name} support?`, a: m.shippingModel },
      { q: `Where are ${m.name}'s warehouses?`, a: m.warehouseLocations },
      { q: 'Should I use warehouse or JIT?', a: 'Hybrid — hero SKUs (top 30 by velocity) in warehouse for ranking + delivery-speed boost, slow-movers via JIT for capital efficiency.' },
      { q: `What's the SLA for JIT dispatch?`, a: '48–72 hours from order confirmation to handover to marketplace-approved carrier. Over 72 hours drops seller score.' },
      { q: 'Can Zaroori Retail set up warehouse operations?', a: `Yes — we handle category-buyer intro, SKU selection, barcoding + packaging spec, intake compliance, and inventory replenishment cadence for ${m.name} warehouse.` },
    ],
  }),

  'api-integration': (m) => ({
    aiSummary: {
      answer: `${m.name} exposes ${m.apiStatus}. Core integration surfaces: orders (pull + acknowledge), inventory (push levels), catalog (bulk XLSX + API), returns (webhook), settlements (statement download). Brands without OMS integration operate at 3–5x higher error rate + 40–60% more manual effort. Zaroori Jini OMS provides native ${m.name} connector with 15-minute sync.`,
      bullets: [
        `API surface: ${m.apiStatus}`,
        'Core endpoints: Orders, Inventory, Catalog, Returns, Settlement',
        'Auth: OAuth 2.0 / API key + HMAC signature',
        'Rate limits: 60–300 requests/min per endpoint',
        'Zaroori Jini OMS native connector: 15-min sync + auto-retry',
      ],
      keyFacts: [
        { label: 'API Surface', value: m.apiStatus },
        { label: 'Authentication', value: 'OAuth 2.0 / API key + HMAC' },
        { label: 'Rate Limit', value: '60–300 req/min' },
        { label: 'Webhook Events', value: 'Orders, Returns, Cancellations' },
        { label: 'Data Format', value: 'JSON + XLSX + XML' },
        { label: 'Updated', value: 'February 2026' },
      ],
    },
    sections: [
      { type: 'para', body: `${m.name}'s ${m.apiStatus} is production-grade but under-documented. Brands attempting DIY integration hit 3 common walls: (1) auth rotation not handled → tokens expire mid-sync, (2) rate limits triggered → orders queue up, (3) webhook delivery not idempotent → duplicate order creation. A proven OMS connector (like Zaroori Jini OMS) abstracts all three.` },
      { type: 'h2', text: 'Core API endpoints' },
      { type: 'table', headers: ['Endpoint', 'Purpose', 'Rate Limit', 'Auth'], rows: [
        ['Orders — pull', 'Fetch new + updated orders', '120/min', 'OAuth 2.0'],
        ['Orders — acknowledge', 'Confirm order acceptance', '300/min', 'OAuth 2.0'],
        ['Inventory — push', 'Update stock levels per SKU', '60/min', 'OAuth 2.0'],
        ['Catalog — create/update', 'Add/edit SKU listings', '30/min', 'OAuth 2.0'],
        ['Returns — webhook', 'Return event stream', 'Event-driven', 'HMAC signature'],
        ['Settlement — download', 'Weekly statement fetch', '10/min', 'OAuth 2.0'],
      ]},
      { type: 'h2', text: 'Integration architecture (recommended)' },
      { type: 'steps', steps: [
        { title: 'OMS as source of truth', body: `Never sync ${m.name} directly to Shopify/WooCommerce. Route everything through an OMS to prevent double-sales.` },
        { title: 'Idempotent webhook handlers', body: 'Use marketplace order ID as idempotency key. Deduplicate at DB level, not app level.' },
        { title: 'Auth rotation cron', body: 'Refresh OAuth tokens 24 hours before expiry. Never wait for a 401.' },
        { title: 'Rate limit backpressure', body: 'Exponential backoff on 429s. Queue writes when approaching rate limit.' },
        { title: 'Observability', body: 'Log every API call + response. Alert on error rate > 0.5% or latency > 2s.' },
      ]},
      { type: 'h2', text: `Why Zaroori Jini OMS is the recommended ${m.name} integration path` },
      { type: 'bullets', items: [
        `Native ${m.name} connector with 15-minute inventory sync + real-time order webhook`,
        'Idempotent order ingestion — zero duplicate orders across 4,000+ brands',
        'Auth rotation, rate limits, and retries handled automatically',
        `Multi-marketplace: same OMS syncs ${m.name} + 12 other marketplaces`,
        'ERP integration: SAP, Oracle NetSuite, Zoho, Tally',
      ]},
    ],
    faqs: [
      { q: `Does ${m.name} have a public API?`, a: m.apiStatus },
      { q: `Can I integrate ${m.name} with my Shopify store directly?`, a: `Not recommended — direct sync causes double-sales when the same SKU sells on both channels simultaneously. Route through an OMS.` },
      { q: `What is ${m.name}'s API rate limit?`, a: '60–300 requests/min depending on endpoint. Bulk endpoints (catalog upload) have lower limits.' },
      { q: `How does Zaroori Jini OMS integrate with ${m.name}?`, a: `15-minute inventory sync, real-time order webhook, native returns handling, and weekly settlement reconciliation — all pre-built.` },
      { q: `How long does ${m.name} API integration take?`, a: 'DIY: 6–12 weeks + 3–6 months to stabilise. With Zaroori Jini OMS: connector live in 3–5 working days, stable from Day 1.' },
    ],
  }),

  integrations: (m) => ({
    aiSummary: {
      answer: `${m.name} integrates with 60+ OMS, ERP, WMS, and storefront platforms — but not all connectors are equal. Native + real-time connectors (like Zaroori Jini OMS) deliver <15-min sync and idempotent order ingestion. Bulk/CSV connectors (Zoho Inventory, spreadsheet-based tools) lag 4–24 hours and cause overselling. For brands doing >₹25L/month on ${m.name}, real-time OMS integration is a hard requirement.`,
      bullets: [
        'Native OMS connectors: 15-min sync (Zaroori Jini OMS, Unicommerce, Vinculum)',
        'Bulk CSV connectors: 4–24 hour lag (Zoho Inventory, spreadsheets)',
        'Storefronts: Shopify, WooCommerce, Magento, Wix — all via OMS',
        'ERP: SAP, Oracle NetSuite, Zoho, Tally',
        'WMS: Increff, WareIQ, Hopstack for physical warehouse ops',
      ],
      keyFacts: [
        { label: 'Total Integrations', value: '60+' },
        { label: 'Recommended Sync Frequency', value: '<15 min' },
        { label: 'GMV Threshold for Real-Time', value: '₹25L/month' },
        { label: 'Native OMS Count', value: '10+' },
        { label: 'Storefronts Supported', value: 'Shopify, Woo, Magento, Wix' },
        { label: 'Updated', value: 'February 2026' },
      ],
    },
    sections: [
      { type: 'para', body: `${m.name}'s integration ecosystem is one of the deepest in Indian e-commerce — but choosing the wrong integration path costs brands 4–8% of GMV in oversells + reconciliation errors. The right integration path depends on GMV band, SKU count, and multi-marketplace strategy.` },
      { type: 'h2', text: 'Integration landscape by category' },
      { type: 'table', headers: ['Category', 'Examples', 'Best For'], rows: [
        ['OMS (Order Management)', 'Zaroori Jini OMS, Unicommerce, Vinculum, Browntape, EasyEcom', 'Multi-marketplace real-time sync'],
        ['ERP', 'SAP Business One, Oracle NetSuite, Zoho, Tally', 'Financial + inventory accounting'],
        ['WMS (Warehouse)', 'Increff, WareIQ, Hopstack, ShipRocket Fulfilment', 'Physical warehouse operations'],
        ['Storefronts', 'Shopify, WooCommerce, Magento, Wix', 'D2C site + marketplace sync'],
        ['Accounting', 'Tally, Zoho Books, QuickBooks, Vyapar', 'GST + financial reporting'],
        ['Ads Automation', 'Perpetua, Downstream, Jungle Scout', 'PLA + Sponsored Brand'],
      ]},
      { type: 'h2', text: `How to pick the right ${m.name} integration stack` },
      { type: 'steps', steps: [
        { title: 'Under ₹10L/month GMV', body: `Direct ${m.name} seller panel + weekly Tally reconciliation is enough.` },
        { title: '₹10–25L/month GMV', body: 'Add OMS with real-time sync. Zoho Inventory (CSV lag OK) or Browntape work.' },
        { title: '₹25L–1Cr/month GMV', body: 'Real-time OMS mandatory. Zaroori Jini OMS, Unicommerce, or EasyEcom. WMS for warehouse.' },
        { title: '>₹1Cr/month GMV', body: 'Enterprise stack: OMS + ERP + WMS + BI + ads automation. Zaroori Jini OMS + SAP/NetSuite + Increff/WareIQ + Perpetua.' },
      ]},
      { type: 'h2', text: `Zaroori Jini OMS — native ${m.name} integration` },
      { type: 'para', body: `Zaroori Jini OMS is the only Indian OMS with native ${m.name} connector + 15-minute sync + 4,000+ brand track record. It handles orders, inventory, catalog, returns, and settlement in one system — with connectors to Shopify + SAP + Tally + Zoho on the same panel.` },
    ],
    faqs: [
      { q: `How many integrations does ${m.name} support?`, a: '60+ across OMS, ERP, WMS, storefronts, accounting, and ads automation.' },
      { q: `What is the best ${m.name} OMS?`, a: `Depends on GMV: <₹25L/month → Zoho Inventory or Browntape; ₹25L–1Cr → Zaroori Jini OMS or Unicommerce; >₹1Cr → Zaroori Jini OMS + SAP/NetSuite.` },
      { q: `Does Shopify integrate with ${m.name}?`, a: `Not directly. Route via OMS to prevent oversells. Zaroori Jini OMS supports Shopify + ${m.name} + 12 other marketplaces on one panel.` },
      { q: `Can I integrate ${m.name} with Tally / Zoho Books?`, a: 'Yes — via OMS. Zaroori Jini OMS pushes daily transactions to Tally/Zoho Books with correct GST + TDS/TCS attribution.' },
      { q: `Can Zaroori Retail set up ${m.name} integrations?`, a: `Yes — we handle full stack: OMS + ERP + WMS + storefront + ads automation for ${m.name} + all other marketplaces you sell on.` },
    ],
  }),

  automation: (m) => ({
    aiSummary: {
      answer: `${m.name} sellers running at ₹25L+/month GMV waste 30–50 hours/week on manual tasks — inventory sync, order processing, price updates, ad bidding, reconciliation. Automation reclaims 25–40 hours/week + eliminates 90% of "human error" oversells + wrong prices + missed payments. AI-driven automation (repricing, bid management, demand forecasting) adds 5–12% margin.`,
      bullets: [
        'Manual hours saved: 25–40/week with automation',
        'Human-error oversells eliminated: 90%',
        'AI margin uplift: 5–12% via repricing + bid + forecast',
        'Core automations: inventory sync, order routing, pricing, ad bidding, reconciliation',
        'Zaroori Jini OMS + AI stack: 20+ automations pre-built',
      ],
      keyFacts: [
        { label: 'Manual Hours Saved', value: '25–40/week' },
        { label: 'Error Rate Reduction', value: '90%' },
        { label: 'AI Margin Uplift', value: '5–12%' },
        { label: 'Core Automations', value: '10+' },
        { label: 'ROI Payback', value: '2–4 months' },
        { label: 'Updated', value: 'February 2026' },
      ],
    },
    sections: [
      { type: 'para', body: `${m.name} automation isn't about "eliminating people" — it's about eliminating human bottlenecks so people can focus on category-buyer relationships, brand storytelling, and growth. At ₹25L+/month GMV, the ROI on automation is 3–5x within the first 6 months.` },
      { type: 'h2', text: `Top 10 automations for ${m.name} sellers` },
      { type: 'table', headers: ['Automation', 'Impact', 'Tool'], rows: [
        ['Real-time inventory sync', 'Zero oversells', 'Zaroori Jini OMS'],
        ['Order acknowledgment', '<5 min from order to accept', 'Zaroori Jini OMS'],
        ['AI repricing', '5–8% margin uplift', 'Zaroori Jini OMS AI'],
        ['PLA bid management', '20–35% ROAS lift', 'Perpetua / Zaroori Jini OMS AI'],
        ['Demand forecasting', 'Avoid stock-outs + dead stock', 'Zaroori Jini OMS AI'],
        ['Weekly reconciliation', '2–8% GMV recovery', 'Zaroori Jini OMS Reconciler'],
        ['Return prevention', 'Fit chart + description checks', 'Zaroori Jini OMS + custom logic'],
        ['GST / TDS filing', 'Zero manual data entry', 'Zoho Books + Zaroori Jini connector'],
        ['Product content sync', 'Same catalog across channels', 'Zaroori Jini PIM'],
        ['Weekly MOM auto-report', 'Category-buyer + management view', 'Zaroori Jini OMS Reports'],
      ]},
      { type: 'h2', text: 'AI-first automation stack' },
      { type: 'steps', steps: [
        { title: 'Ingestion layer', body: `Zaroori Jini OMS pulls orders + inventory + prices + ad performance from ${m.name} every 15 min.` },
        { title: 'Decision layer', body: 'AI models score every SKU × channel × price × ad-bid decision every 6 hours.' },
        { title: 'Execution layer', body: 'Winning decisions auto-executed: price update, bid adjustment, replenishment order, dispute filing.' },
        { title: 'Feedback loop', body: 'Every outcome (conversion, return, ad ROAS) fed back to models → continuously improving decisions.' },
      ]},
      { type: 'h2', text: 'When to invest in automation' },
      { type: 'bullets', items: [
        '₹25L+/month GMV → real-time OMS + basic automation (mandatory)',
        '₹50L+/month → add AI repricing + ad automation',
        '₹1Cr+/month → full AI stack + forecasting + reconciliation + BI',
        '₹5Cr+/month → enterprise: OMS + ERP + WMS + custom AI models',
      ]},
    ],
    faqs: [
      { q: `What automations should I use on ${m.name}?`, a: 'Real-time inventory sync (mandatory), order auto-acknowledgment, AI repricing, PLA bid management, weekly reconciliation, demand forecasting.' },
      { q: 'How much time does automation save?', a: `25–40 hours/week for a ₹25L+/month ${m.name} seller. Payback: 2–4 months.` },
      { q: 'Does Zaroori Jini OMS have AI automation?', a: `Yes — 20+ pre-built automations covering ${m.name} pricing, ads, inventory, returns, and reconciliation. Same stack extends to your other marketplaces.` },
      { q: 'Is automation risky?', a: 'Not with guardrails: price floor/ceiling, ad budget caps, inventory safety stock, and human-in-loop for edge cases. Zaroori Jini OMS ships with sensible defaults.' },
      { q: 'Can Zaroori Retail deploy automation for me?', a: `Yes — we deploy Zaroori Jini OMS + custom AI models + integrations for ${m.name} + all other marketplaces + your ERP + storefront + accounting stack in 3–6 weeks.` },
    ],
  }),
};

// ============================================================================
// Helper: build category-specific commission table rows
// ============================================================================
function buildCategoryCommissionRows(m) {
  if (m.focus.toLowerCase().includes('fashion')) {
    return [
      ['Ethnic wear', '22–28%', 'Category-buyer flexible'],
      ['Western wear', '25–32%', 'Highest category volume'],
      ['Footwear', '20–30%', 'Higher for luxury'],
      ['Accessories', '18–25%', 'Lowest volume, best margin'],
    ];
  }
  if (m.focus.toLowerCase().includes('beauty')) {
    return [
      ['Skincare (mass)', '25–32%', 'Highest category volume'],
      ['Skincare (premium)', '32–42%', 'Category-buyer negotiable'],
      ['Makeup', '28–38%', 'Requires demonstrator SKUs'],
      ['Fragrance', '38–48%', 'Highest commission tier'],
    ];
  }
  if (m.focus.toLowerCase().includes('quick commerce')) {
    return [
      ['Grocery staples', '12–15%', 'Volume category'],
      ['FMCG snacks', '15–20%', 'Best-selling category'],
      ['Personal care', '18–22%', 'Premium band'],
      ['F&B ready-to-eat', '20–25%', 'Highest AOV'],
    ];
  }
  return [
    ['Category A', '15–25%', 'Volume'],
    ['Category B', '20–30%', 'Balance'],
    ['Category C', '25–35%', 'Premium'],
  ];
}

// ============================================================================
// PUBLIC API — build all 70 records at module load
// ============================================================================
/**
 * A2 Phase 1 (Oct 2026) — per-URL overrides so the two quick-commerce fee
 * pages own "commission / charges / rate card / deductions" while the matching
 * /answers/*-onboarding-cost page owns "onboarding fees / agency cost".
 * Only these two slugs change; the other 68 topic pages are untouched.
 */
const A2_TOPIC_OVERRIDES = {
  'blinkit-commission': {
    h1: 'Blinkit Commission, Margins & Rate Card (2026)',
    metaTitle: 'Blinkit Commission & Rate Card 2026 — Seller Charges Explained',
    metaDescription: 'Blinkit commission percentages, category rate card, platform charges and margin maths for brands — what Blinkit deducts, line by line (2026).',
  },
  'zepto-commission': {
    h1: 'Zepto Commission, OB Fee Deduction & Margins',
    metaTitle: 'Zepto Commission & OB Fee Deduction Explained (2026)',
    metaDescription: 'Zepto commission rates, OB fee deduction, platform charges and a margin calculator view for suppliers — exactly what Zepto deducts per order (2026).',
  },
};

const buildRecord = (m, topicSlug) => {
  const t = TOPIC_META[topicSlug];
  const gen = generators[topicSlug](m);
  const o = A2_TOPIC_OVERRIDES[`${m.slug}-${topicSlug}`] || {};
  return {
    slug: `${m.slug}-${topicSlug}`,
    marketplaceSlug: m.slug,
    marketplace: m,
    topicSlug,
    topicLabel: t.label,
    h1: `${m.name} ${t.titleSuffix}`,
    metaTitle: `${m.name} ${t.titleSuffix} | Zaroori Retail`,
    metaDescription: `${m.name} — ${t.metaSuffix}`,
    updated: 'February 2026',
    aiSummary: gen.aiSummary,
    sections: gen.sections,
    faqs: gen.faqs,
    ...o,
  };
};

export const TOPIC_PAGES = MARKETPLACES.flatMap((m) =>
  TOPIC_KEYS.map((topicSlug) => buildRecord(m, topicSlug))
);

export const TOPIC_PAGES_BY_SLUG = TOPIC_PAGES.reduce((acc, p) => {
  acc[p.slug] = p;
  return acc;
}, {});

export const TOPIC_PATHS = TOPIC_PAGES.map((p) => `/${p.slug}`);

// Related sub-topics: same marketplace, other topics
export const getRelatedTopics = (page) => {
  return TOPIC_PAGES.filter(
    (p) => p.marketplaceSlug === page.marketplaceSlug && p.topicSlug !== page.topicSlug
  );
};

// Cross-marketplace comparable pages: same topic, other marketplaces
export const getCrossMarketplaceComparables = (page) => {
  return TOPIC_PAGES.filter(
    (p) => p.topicSlug === page.topicSlug && p.marketplaceSlug !== page.marketplaceSlug
  );
};
